/**
 * Marketplace Event Tracking Helper
 *
 * Wysyła anonimowe eventy do POST /api/public/events.
 * Fire-and-forget — nie blokuje UX.
 */
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Stable anonymous client ID (UUID v4) trzymany w localStorage.
// Backend używa tego jako fingerprint dla favorites — jest stabilny niezależnie od IP/k8s ingress.
const CLIENT_ID_KEY = 'apisai.client_id';
function getClientId() {
  try {
    let id = localStorage.getItem(CLIENT_ID_KEY);
    if (!id) {
      // RFC4122-ish UUID v4 (crypto.randomUUID() preferred when available)
      id = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : 'cid-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
      localStorage.setItem(CLIENT_ID_KEY, id);
    }
    return id;
  } catch {
    return 'anon';
  }
}

export function clientHeaders() {
  return { 'X-Client-Id': getClientId() };
}

// Local cache for favorites (mirrors backend, used as fallback if backend hiccups)
const FAV_CACHE_KEY = 'apisai.favorites';
function getCachedFavorites() {
  try {
    const raw = localStorage.getItem(FAV_CACHE_KEY);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch {
    return new Set();
  }
}
function setCachedFavorites(set) {
  try {
    localStorage.setItem(FAV_CACHE_KEY, JSON.stringify(Array.from(set)));
  } catch { /* ignore quota errors */ }
}

export async function trackEvent(type, payload = {}) {
  try {
    await axios.post(`${API}/public/events`, { type, payload }, { timeout: 4000, headers: clientHeaders() });
  } catch {
    /* silent — tracking nigdy nie blokuje UX */
  }
}

export async function toggleFavorite(entityType, entityId) {
  // Update cache optimistically
  const cache = getCachedFavorites();
  const wasFavorite = cache.has(entityId);
  if (wasFavorite) cache.delete(entityId);
  else cache.add(entityId);
  setCachedFavorites(cache);

  try {
    const res = await axios.post(`${API}/public/favorites/toggle`, {
      entity_type: entityType,
      entity_id: entityId,
    }, { timeout: 4000, headers: clientHeaders() });
    const favorited = res.data?.favorited ?? !wasFavorite;
    // Sync cache with backend truth
    const c2 = getCachedFavorites();
    if (favorited) c2.add(entityId);
    else c2.delete(entityId);
    setCachedFavorites(c2);
    // Powiadom inne komponenty (np. TopNavBar badge) o zmianie
    try {
      window.dispatchEvent(new CustomEvent('apisai:favorites-changed', {
        detail: { count: c2.size, favorited, entityId },
      }));
    } catch { /* SSR safe */ }
    return favorited;
  } catch {
    // Backend hiccup — keep optimistic local state
    try {
      const c3 = getCachedFavorites();
      window.dispatchEvent(new CustomEvent('apisai:favorites-changed', {
        detail: { count: c3.size, favorited: !wasFavorite, entityId },
      }));
    } catch { /* SSR safe */ }
    return !wasFavorite;
  }
}

export async function getFavorites() {
  // Prefer backend (so sync across devices for same client_id), fallback to cache
  try {
    const res = await axios.get(`${API}/public/favorites/list`, { timeout: 4000, headers: clientHeaders() });
    const items = res.data?.items || [];
    const set = new Set(items.map(i => i.entity_id));
    setCachedFavorites(set);
    return items;
  } catch {
    return Array.from(getCachedFavorites()).map(id => ({ entity_id: id }));
  }
}

export async function submitLead({ entityType, entityId, name, email, phone, message, source = 'catalog_card' }) {
  try {
    const payload = {
      entity_type: entityType,
      entity_id: entityId,
      name,
      phone,
      message,
      source,
    };
    if (email) payload.email = email;
    const res = await axios.post(`${API}/public/leads`, payload, { timeout: 8000, headers: clientHeaders() });
    trackEvent('lead_click', { entity_type: entityType, entity_id: entityId });
    return { ok: true, data: res.data };
  } catch (e) {
    return { ok: false, error: e?.response?.data?.detail || 'Coś poszło nie tak' };
  }
}
